import { createRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { beforeLoadGeneral } from '@Router/middleware.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { SplashScreen } from '@Components/splash/SplashScreen.tsx';
import { router } from '@Routes/__root.tsx';
import { GeneralSpinner } from '@Components/generalSpinner.tsx';
import { WelcomePage } from '@Pages/passwordless/welcome/WelcomePage.tsx';
import { LoginPage } from '@Pages/loginPage/Login.tsx';
import { fetchCountries } from '@Hooks/api/fetchCountries.api.ts';
import { HomeAddressPage, IdentityPage, PersonalDetailsPage, PhonePage } from '../index.ts';
import { RoutesMap } from '../router.types.ts';
import { routesModel } from '../router.consts.ts';
import { redirectToWebAppLoginPage } from '../../utils.ts';
import { rootRoute } from './rootRoute.tsx';

const getObData = useBoundStore.getState().onboardingSlice.getOnboardingData;

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (getObData().isLoginProcessFinished) {
      throw router.navigate({ to: routesModel[RoutesMap.WELCOME].path });
    }
  },
  component: () => <SplashScreen />,
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.WELCOME].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.WELCOME,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  validateSearch: (search: Record<string, unknown>): { email?: string } => {
    const email = typeof search.email === 'string' ? search.email : undefined;
    if (email) {
      const customer = useBoundStore.getState().getCustomerData();
      if (!customer.customerData.email) {
        customer.customerData.email = email;
        useBoundStore.getState().setCustomerData(customer);
      }
    }
    return { email };
  },
  component: () => <Suspense fallback={<GeneralSpinner />}>{<WelcomePage />}</Suspense>,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.LOGIN].path,
  beforeLoad: ({ search }) => {
    // This is to ensure all previous campaigns with old links are redirected to the new flow, removed the beforeloadgeneral logic
    const email = typeof search.email === 'string' ? search.email : undefined;
    redirectToWebAppLoginPage(email);
  },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: typeof search.email === 'string' ? search.email : undefined,
    };
  },
  component: () => <LoginPage />,
});

const phoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.PHONE].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.PHONE, shouldUpdateServerNavStack: true });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <PhonePage />
    </Suspense>
  ),
});

const identityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.IDENTITY].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({ page: RoutesMap.IDENTITY, shouldUpdateServerNavStack: true, force: true });
    await context.queryClient.prefetchQuery({
      queryKey: ['countries', false, false],
      queryFn: () => fetchCountries(false, false),
      staleTime: Infinity,
    });
  },
  loader: async () => fetchCountries(false, false),
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <IdentityPage />
    </Suspense>
  ),
});

const HomeAddressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.HOME_ADDRESS].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({ page: RoutesMap.HOME_ADDRESS, shouldUpdateServerNavStack: true });
    await context.queryClient.prefetchQuery({
      queryKey: ['countries', true, false],
      queryFn: () => fetchCountries(true, false),
      staleTime: Infinity,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <HomeAddressPage />
    </Suspense>
  ),
});
const PersonalDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.PERSONAL_DETAILS].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.PERSONAL_DETAILS, shouldUpdateServerNavStack: true });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <PersonalDetailsPage />
    </Suspense>
  ),
});
export const personalRoutes = [
  indexRoute,
  welcomeRoute,
  phoneRoute,
  identityRoute,
  HomeAddressRoute,
  PersonalDetailsRoute,
  loginRoute,
];
